import React from 'react';
import useDataFetching from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import EmptyState from '../shared/EmptyState';
import StatCard from '../shared/StatCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getRemediationStats = async () => {
  const response = await fetch(`${BACKEND_URL}/api/remediation/stats`);
  if (!response.ok) {
    throw new Error('Failed to fetch remediation stats');
  }
  return response.json();
};

const Remediation = () => {
  // Fetch stats (no auto-refresh)
  const { data: stats, loading, error, refetch } = useDataFetching(
    getRemediationStats,
    []
  );

  if (loading && !stats) {
    return <LoadingState message="Loading Remediation..." fullScreen />;
  }

  if (error && !stats) {
    return <ErrorState message={error} onRetry={refetch} fullScreen />;
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Automated Remediation</h1>
            <p className="text-gray-600 mt-1">
              Configure and track automated remediation workflows
            </p>
          </div>
          <div>
            <button className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium">
              + New Rule
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            title="Total Actions"
            value={stats?.total_actions || 0}
            subtitle="Available actions"
          />
          
          <StatCard
            title="Active Rules"
            value={`${stats?.active_rules || 0} out of ${stats?.total_rules || 0}`}
            subtitle="Monitoring"
          />
          
          <StatCard
            title="Executions"
            value={`${stats?.executions || 0} (${stats?.successful_executions || 0} successful)`}
            subtitle="Total runs"
          />
          
          <StatCard
            title="Success Rate"
            value={`${stats?.success_rate || 0}%`}
            subtitle="Execution success"
          />
        </div>

        {/* Action Library */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Action Library</h2>
          <EmptyState
            title="No actions available"
            description="Actions will appear here once configured"
            icon={() => <span className="text-5xl">⚙️</span>}
          />
        </div>

        {/* Remediation Rules */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Remediation Rules</h2>
          <EmptyState
            title="No remediation rules configured"
            description="Create one to automate responses!"
            icon={() => <span className="text-5xl">🛡️</span>}
            actionLabel="Create Rule"
          />
        </div>

        {/* Recent Executions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Executions</h2>
          <EmptyState
            title="No executions yet"
            description="Rules will trigger automatically when anomalies are detected."
            icon={() => <span className="text-5xl">⏱️</span>}
          />
        </div>
      </div>
    </div>
  );
};

export default Remediation;
